import {Component, OnInit} from '@angular/core';
import { NavigationEnd, Router } from "@angular/router";
import {catchError, filter, forkJoin, of, switchMap, tap} from 'rxjs';
import { ProfileService } from 'src/app/auth_profile/services/profile.service';
import { ThemeService } from 'src/app/services/theme.service';
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";
import {LogsService} from "../../utils/services/logs.service";
import {LangService} from "../../services/lang.service";
import {AuthorizationServiceProvider} from "../../auth_profile/services/authorization-service.provider";
import * as LessonsV3 from '../../col/model/v3/lessons';
import {IntroductionState} from '../../col/model/v3/lessons';
import {environment} from "../../../environments/environment";

@Component({
    selector: 'app-student-dashboard-layout',
    templateUrl: './student-dashboard-layout.component.html',
    styleUrls: ['./student-dashboard-layout.component.scss'],
    standalone: false
})
export class StudentDashboardLayoutComponent implements OnInit {

  private static modalIntroductionStates = [
    IntroductionState.Language,
    IntroductionState.Course,
    IntroductionState.PersonalProfile
  ];

  activePath: string;
  activeSegments: string[];
  theme: string;
  schoolId: number;
  isAppNative: boolean;
  studentId: number;
  personId: number;
  profilePhoto: string | null;
  technicalProfile: LessonsV3.PersonalSettings;

  constructor(
    private router: Router,
    private profileService: ProfileService,
    public themeService: ThemeService,
    public provider: NativeServiceApiProvider,
    public logs: LogsService,
    private langService: LangService,
    private authService: AuthorizationServiceProvider,
  ) {
    this.activePath = this.normalizePath(this.router.url);
    this.activeSegments = this.activePath ? this.activePath.split('/') : [];

    router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe((e) => {
      this.activePath = this.normalizePath(e.urlAfterRedirects);
      this.activeSegments = this.activePath ? this.activePath.split('/') : [];
    });

    this.langService.initialize();
    this.authService.getCommonRoutines().subscribe(auth =>
      this.isAppNative = auth.isNativeAuthorization()
    );
  }

  ngOnInit(): void {
    this.loadProfilePhoto();
    this.authService.getAuthDetailsService().pipe(
      switchMap((api) => forkJoin([api.getSelfSchoolId(), api.getSelfStudentId(), api.getSelfPersonId()])),
      tap(([schoolId, studentId, personId]) => {
        this.schoolId = schoolId;
        this.studentId = studentId;
        this.personId = personId;
      }),
      switchMap(() => this.loadTechnicalProfile())
    ).subscribe();
    this.themeService.getTheme().subscribe((theme) => (this.theme = theme));
  }

  private loadProfilePhoto(): void {
    this.profileService.getSelfPhoto().pipe(
      tap((photo) => {
        this.profilePhoto = photo?.publicAccessUrl || null;
      }),
      catchError((err) => {
        this.logs.error("got error when asking for self profile data in student dashboard", err);
        return of(null);
      })
    ).subscribe();
  }

  private loadTechnicalProfile() {
    if (!this.personId) {
      return of(null);
    }
    return this.provider.colV3().pipe(
      switchMap((api) => api.getPersonColSettings(this.personId)),
      tap((profile) => {
        this.technicalProfile = profile;
        const introductionState = this.mapIntroductionState(this.technicalProfile?.introductionState);
        if (this.schoolId === 1
          && environment.colFunctionality
          && StudentDashboardLayoutComponent.modalIntroductionStates.includes(introductionState)) {
          this.router.navigate(['/student/reserve-lesson']).then();
        }
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  private mapIntroductionState(value?: string | null): IntroductionState {
    if (!value) {
      return IntroductionState.Course;
    }
    const enumValues = Object.values(IntroductionState) as string[];
    if (enumValues.includes(value)) {
      return value as IntroductionState;
    }
    return IntroductionState.Course;
  }

  private normalizePath(url: string | null | undefined): string {
    const value = url ?? '';
    const queryIdx = value.indexOf('?');
    return queryIdx >= 0 ? value.substring(0, queryIdx) : value;
  }
}
